import {
  leadsTasksListOptions,
  opportunitiesTasksListOptions,
  ReturnTaskReadable
} from '@crm/api'
import { Text } from '@crm/components'
import { OpportunityContext } from '@crm/contexts/OpportunityContext'
import { TagPriorityByValue, TagStatusByValue } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Flex, ModalProps, TableColumnsType, Tag } from 'antd'
import { MenuProps } from 'antd/lib'
import { useTheme } from 'antd-style'
import { Clock, Copy, Edit2, TickCircle, Trash } from 'iconsax-react'
import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddEditTask } from './AddEditTask'
import { CompleteTask } from './CompleteTask'
import { DeleteTask } from './DeleteTask'
import { DuplicateTask } from './DuplicateTask'
import { TasksHeader } from './TaskHeader'
import { TasksFilter } from './TasksFilter'
import { UnCompleteTask } from './UncompleteTask'

type ModalType = 'edit' | 'delete' | 'complete' | 'unComplete' | 'duplicate'

const modalTypes: Record<
  ModalType,
  React.ComponentType<
    {
      task: ReturnTaskReadable | null
      onCancel: () => void
    } & ModalProps
  >
> = {
  edit: AddEditTask,
  delete: DeleteTask,
  complete: CompleteTask,
  unComplete: UnCompleteTask,
  duplicate: DuplicateTask
}

export const TasksCol = () => {
  const { t } = useLocales()
  const { id, opportunityId } = useParams()
  const theme = useTheme()
  const { params } = useQueryUpdate()

  const [modalState, setModalState] = useState<{
    type: ModalType | null
    task: ReturnTaskReadable | null
  }>({ task: null, type: null })

  const { type } = useContext(OpportunityContext)

  const { data: leadData, isLoading: isLeadLoading } = useQuery({
    ...leadsTasksListOptions({
      path: {
        lead_id: String(id)
      },
      query: {
        ...params,
        lead: [Number(id)]
      }
    }),
    enabled: !!id && type === 'lead'
  })

  const { data: opportunityData, isLoading: isOpportunityLoading } = useQuery({
    ...opportunitiesTasksListOptions({
      path: {
        opportunity_id: String(opportunityId)
      },
      query: {
        ...params
      }
    }),
    enabled: !!opportunityId && type === 'opportunity'
  })

  const data = leadData || opportunityData
  const isLoading = isLeadLoading || isOpportunityLoading

  const actionItems = (task: ReturnTaskReadable): MenuProps['items'] => [
    {
      label: (
        <Text type={task?.completed_at ? 'warning' : 'success'}>
          {task?.completed_at ? t('filter.uncompleted') : t('filter.completed')}
        </Text>
      ),
      key: 'status',
      icon: task?.completed_at ? (
        <Clock color={theme.colorWarning} size={20} />
      ) : (
        <TickCircle color={theme.colorSuccess} size={20} />
      ),
      onClick: () =>
        task?.completed_at
          ? setModalState({ task, type: 'unComplete' })
          : setModalState({ task, type: 'complete' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ task, type: 'edit' })
    },
    {
      label: t('buttons.duplicate'),
      key: 'duplicate',
      icon: <Copy size={20} />,
      onClick: () => setModalState({ task, type: 'duplicate' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ task, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<ReturnTaskReadable> = [
    {
      title: t('nomenclature.name'),
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: t('contracts.owner'),
      dataIndex: ['owner', 'name'],
      key: 'owner_name',
      render: (value) => value ?? '-'
    },
    {
      title: t('task.task_type'),
      dataIndex: ['type', 'name'],
      key: 'type_name',
      render: (value) => value ?? '-'
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, { created_at }) => <DayLabel date={created_at} />
    },
    {
      title: t('billing.deadline'),
      dataIndex: 'deadline',
      key: 'deadline',
      render: (_, { deadline }) => <DayLabel date={deadline} />
    },
    {
      title: t('task.priority'),
      dataIndex: 'priority',
      key: 'priority',
      render: (value) => {
        const properties = TagPriorityByValue(t)[value]
        if (!properties) return null

        return <Tag color={properties?.color}>{properties?.title}</Tag>
      }
    },
    {
      title: t('task.status'),
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (value) => {
        const properties = TagStatusByValue(t)[value ? 1 : 0]
        if (!properties) return null

        return <Tag color={properties?.color}>{properties?.title}</Tag>
      }
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, task) => <TableActions options={actionItems(task)} />
    }
  ]

  const actionComponent = useMemo(() => {
    const Component = modalState.type ? modalTypes[modalState.type] : null

    return Component && modalState.task ? (
      <Component
        open
        task={modalState.task}
        onCancel={() => setModalState({ task: null, type: null })}
      />
    ) : null
  }, [modalState])

  return (
    <Flex vertical gap={16}>
      <TableContainer
        columns={columns}
        data={data}
        loading={isLoading}
        leftChildren={<TasksFilter />}
        rightChildren={<TasksHeader />}
      />
      {actionComponent}
    </Flex>
  )
}
